import {
  collection,
  onSnapshot,
} from 'firebase/firestore'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { firestore } from '../core'
import { useAuth } from '../core/providers/auth'

export type WorkoutType = {
  id: string
  name: string
  bookable: boolean
  active: boolean
  image?: string
  color?: string
  sortOrder?: number
}

export type WorkoutLocation = {
  id: string
  name: string
  active: boolean
  sortOrder?: number
}

export type WorkoutEquipment = {
  id: string
  name: string
  active: boolean
  sortOrder?: number
}

export const WORKOUT_ASSETS = [
  '/assets/bodypump.png',
  '/assets/buildandburn.png',
  '/assets/circletraining.png',
  '/assets/gym.png',
  '/assets/opengym.png',
  '/assets/running.png',
  '/assets/spinning.png',
]

export const DEFAULT_WORKOUT_ASSET = '/assets/gym.png'

export const WORKOUT_COLORS = [
  '#b67943',
  '#2f7d80',
  '#2f5f8f',
  '#7a5c99',
  '#6f7a3a',
  '#a4554d',
  '#8c6b2f',
  '#3f4f52',
]

export const DEFAULT_WORKOUT_COLOR = '#b67943'

type WorkoutSettingsContext = {
  workoutTypes: WorkoutType[]
  activeWorkoutTypes: WorkoutType[]
  locations: WorkoutLocation[]
  activeLocations: WorkoutLocation[]
  equipment: WorkoutEquipment[]
  activeEquipment: WorkoutEquipment[]
  loading: boolean
  error: string | null
  getWorkoutType: (id: string) => WorkoutType
  getWorkoutTypeName: (id: string) => string
  isWorkoutTypeBookable: (id: string) => boolean
}

export const DEFAULT_WORKOUT_TYPES: WorkoutType[] = [
  {
    id: 'bodypump',
    name: 'Bodypump',
    bookable: true,
    active: true,
    image: '/assets/bodypump.png',
    color: DEFAULT_WORKOUT_COLOR,
    sortOrder: 10,
  },
  {
    id: 'buildandburn',
    name: 'Bygg och bränn',
    bookable: true,
    active: true,
    image: '/assets/buildandburn.png',
    color: '#2f7d80',
    sortOrder: 20,
  },
  {
    id: 'circletraining',
    name: 'Cirkelträning',
    bookable: true,
    active: true,
    image: '/assets/circletraining.png',
    color: '#7a5c99',
    sortOrder: 30,
  },
  {
    id: 'gym',
    name: 'Gym',
    bookable: true,
    active: true,
    image: DEFAULT_WORKOUT_ASSET,
    color: '#2f5f8f',
    sortOrder: 40,
  },
  {
    id: 'opengym',
    name: 'Open Gym',
    bookable: true,
    active: true,
    image: '/assets/opengym.png',
    color: '#6f7a3a',
    sortOrder: 50,
  },
  {
    id: 'running',
    name: 'Löpning',
    bookable: true,
    active: true,
    image: '/assets/running.png',
    color: '#a4554d',
    sortOrder: 60,
  },
  {
    id: 'spinning',
    name: 'Spinning',
    bookable: true,
    active: true,
    image: '/assets/spinning.png',
    color: '#8c6b2f',
    sortOrder: 70,
  },
]

const fallbackWorkoutType = DEFAULT_WORKOUT_TYPES[2]

const WorkoutSettings = createContext({} as WorkoutSettingsContext)

function sortSettings<T extends { name: string; sortOrder?: number }>(items: T[]) {
  return [...items].sort((a, b) => {
    const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER
    const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER

    if (orderA !== orderB) return orderA - orderB

    return a.name.localeCompare(b.name, 'sv')
  })
}

function mergeWorkoutTypes(remoteTypes: WorkoutType[]) {
  const merged = new Map(DEFAULT_WORKOUT_TYPES.map((type) => [type.id, type]))

  remoteTypes.forEach((type) => {
    const defaultType = merged.get(type.id)
    merged.set(type.id, {
      ...defaultType,
      ...type,
      id: type.id,
      name: type.name || defaultType?.name || type.id,
      bookable: type.bookable ?? defaultType?.bookable ?? true,
      active: type.active ?? defaultType?.active ?? true,
    })
  })

  return sortSettings(Array.from(merged.values()))
}

export default function WorkoutSettingsProvider({
  children,
}: {
  children: ReactNode
}) {
  const { user } = useAuth()
  const [remoteTypes, setRemoteTypes] = useState<WorkoutType[]>([])
  const [locations, setLocations] = useState<WorkoutLocation[]>([])
  const [equipment, setEquipment] = useState<WorkoutEquipment[]>([])
  const [loadingTypes, setLoadingTypes] = useState(true)
  const [loadingLocations, setLoadingLocations] = useState(true)
  const [loadingEquipment, setLoadingEquipment] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      setRemoteTypes([])
      setLocations([])
      setEquipment([])
      setLoadingTypes(false)
      setLoadingLocations(false)
      setLoadingEquipment(false)
      return
    }

    setLoadingTypes(true)
    setLoadingLocations(true)
    setLoadingEquipment(true)

    const unsubTypes = onSnapshot(
      collection(firestore, 'workoutTypes'),
      (snapshot) => {
        setRemoteTypes(
          snapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              } as WorkoutType)
          )
        )
        setLoadingTypes(false)
      },
      (error) => {
        console.log(error.message)
        setError(error.message)
        setLoadingTypes(false)
      }
    )

    const unsubLocations = onSnapshot(
      collection(firestore, 'locations'),
      (snapshot) => {
        setLocations(
          sortSettings(
            snapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  active: true,
                  ...doc.data(),
                } as WorkoutLocation)
            )
          )
        )
        setLoadingLocations(false)
      },
      (error) => {
        console.log(error.message)
        setError(error.message)
        setLoadingLocations(false)
      }
    )

    const unsubEquipment = onSnapshot(
      collection(firestore, 'equipment'),
      (snapshot) => {
        setEquipment(
          sortSettings(
            snapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  active: true,
                  ...doc.data(),
                } as WorkoutEquipment)
            )
          )
        )
        setLoadingEquipment(false)
      },
      (error) => {
        console.log(error.message)
        setError(error.message)
        setLoadingEquipment(false)
      }
    )

    return () => {
      unsubTypes()
      unsubLocations()
      unsubEquipment()
    }
  }, [user])

  const workoutTypes = useMemo(
    () => mergeWorkoutTypes(remoteTypes),
    [remoteTypes]
  )

  const value = useMemo(() => {
    const getWorkoutType = (id: string) =>
      workoutTypes.find((type) => type.id === id) ?? {
        ...fallbackWorkoutType,
        id,
        name: id || fallbackWorkoutType.name,
      }

    return {
      workoutTypes,
      activeWorkoutTypes: workoutTypes.filter((type) => type.active),
      locations,
      activeLocations: locations.filter((location) => location.active),
      equipment,
      activeEquipment: equipment.filter((equipment) => equipment.active),
      loading: loadingTypes || loadingLocations || loadingEquipment,
      error,
      getWorkoutType,
      getWorkoutTypeName: (id: string) => getWorkoutType(id).name,
      isWorkoutTypeBookable: (id: string) => getWorkoutType(id).bookable,
    }
  }, [
    equipment,
    error,
    loadingEquipment,
    loadingLocations,
    loadingTypes,
    locations,
    workoutTypes,
  ])

  return (
    <WorkoutSettings.Provider value={value}>
      {children}
    </WorkoutSettings.Provider>
  )
}

export const useWorkoutSettings = () => useContext(WorkoutSettings)
